import { useState, type FormEvent } from 'react';
import PageShell from '@/components/feature/PageShell';
import { usePageMeta } from '@/hooks/usePageMeta';
import { BRAND_NAME } from '@/mocks/categories';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db69n24be24agvuu3hag';

const faqs = [
  { q: '本站可以直接播放视频吗？', a: '不能。播放区域仅为界面演示，本站不提供也不存储任何影视文件。' },
  { q: '为什么有些资源显示暂无？', a: '部分分类下资源较少，可通过筛选或搜索查看其他相关内容。' },
  { q: '收藏记录会同步到其他设备吗？', a: '不会。收藏数据仅保存在当前浏览器本地，清除浏览器数据后会丢失。' },
  { q: '如何反馈内容问题？', a: '可通过本页留言表单提交，我们会尽快核实并处理。' },
];

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function ContactPage() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  usePageMeta({
    title: `联系我们 - ${BRAND_NAME}`,
    description: `通过留言表单联系${BRAND_NAME}团队，反馈内容问题、提出建议或咨询合作，我们会尽快回复。`,
    keywords: `联系我们,${BRAND_NAME},留言,意见反馈,合作咨询`,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('留言已提交，感谢你的反馈！');
      formEl.reset();
      return;
    }

    const name = String(formData.get('name') || '').trim();
    const email = String(formData.get('email') || '').trim();
    const content = String(formData.get('message') || '').trim();

    if (!name) {
      setStatus('error');
      setMessage('请填写你的称呼');
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('请填写有效的邮箱地址');
      return;
    }
    if (!content) {
      setStatus('error');
      setMessage('请填写留言内容');
      return;
    }
    if (content.length > 500) {
      setStatus('error');
      setMessage('留言内容不能超过 500 字');
      return;
    }

    setStatus('loading');
    setMessage('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = String(value).trim();
      if (str === '') return;
      payload.append(key, str);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        setMessage('留言提交成功，感谢你的反馈！');
        formEl.reset();
      } else {
        setStatus('error');
        setMessage(typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后重试');
    }
  };

  return (
    <PageShell>
      <div className="bg-primary-900 text-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-background-100 text-xs">
            <i className="ri-customer-service-2-line"></i>
            联系我们
          </span>
          <h1 className="mt-4 text-3xl md:text-4xl font-bold text-background-50">留言与联系方式</h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base text-background-200 leading-relaxed">
            无论是内容建议、使用问题还是合作咨询，都欢迎通过下方表单或邮箱与我们联系，我们会尽快回复。
          </p>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
        <section>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1 h-7 rounded-full bg-accent-500"></span>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">在线留言</h2>
          </div>

          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
                  你的称呼 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="请输入你的称呼"
                  className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
                  邮箱地址 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="用于接收我们的回复"
                  className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-2">
                留言内容 <span className="text-accent-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={5}
                maxLength={500}
                placeholder="请描述你的建议、问题或合作意向（最多 500 字）"
                className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 resize-none"
              ></textarea>
            </div>

            <div className="field-extra-guard" aria-hidden="true">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-foreground-400">提交后我们会在 1-2 个工作日内回复（演示站点无真实回复）。</p>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex items-center justify-center gap-1.5 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors disabled:opacity-60 cursor-pointer"
              >
                <i className={status === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
                {status === 'loading' ? '提交中...' : '提交留言'}
              </button>
            </div>

            {status === 'success' && (
              <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-md bg-secondary-50 border border-secondary-200 text-sm text-secondary-700">
                <i className="ri-checkbox-circle-line text-lg"></i>
                {message}
              </div>
            )}
            {status === 'error' && (
              <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-700">
                <i className="ri-error-warning-line text-lg"></i>
                {message}
              </div>
            )}
          </form>

          <section className="mt-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1 h-7 rounded-full bg-accent-500"></span>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">常见问题</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq) => (
                <div key={faq.q} className="p-4 md:p-5 bg-background-50 border border-background-200 rounded-lg">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950">{faq.q}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>
        </section>

        <aside className="space-y-4">
          <div className="p-6 bg-background-100 border border-background-200 rounded-lg">
            <h2 className="text-lg font-bold text-foreground-950">联系方式</h2>
            <ul className="mt-4 space-y-3 text-sm text-foreground-700">
              <li className="flex items-start gap-3">
                <i className="ri-mail-line text-accent-500 text-lg mt-0.5"></i>
                <span>
                  <span className="block text-foreground-950 font-medium">邮箱</span>
                  service@gyyy-resource.cn
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-time-line text-accent-500 text-lg mt-0.5"></i>
                <span>
                  <span className="block text-foreground-950 font-medium">服务时间</span>
                  周一至周五 9:00 - 18:00
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-map-pin-line text-accent-500 text-lg mt-0.5"></i>
                <span>
                  <span className="block text-foreground-950 font-medium">内容范围</span>
                  面向全国，线上服务
                </span>
              </li>
            </ul>
          </div>

          <div className="p-6 bg-primary-900 text-background-100 rounded-lg">
            <h2 className="text-lg font-bold text-background-50">温馨提示</h2>
            <p className="mt-3 text-sm leading-relaxed text-background-200">
              本站为影音资源展示与导航平台，播放功能仅为界面演示，不具备真实播放能力。
              请通过正规渠道观看正版影视作品。
            </p>
          </div>
        </aside>
      </div>
    </PageShell>
  );
}